import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Sun, Phone, CalendarCheck, Target, Lightning, ArrowRight,
  CheckCircle, Clock, Buildings, Cube, Kanban, Users,
  ArrowClockwise, Check, WarningCircle
} from "@phosphor-icons/react";

export default function DailyPlannerPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPlan = useCallback(async () => {
    try {
      const { data } = await api.get("/planner");
      setPlan(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  async function completeFollowUp(id) {
    try {
      await api.post(`/planner/follow-up/${id}/complete`);
      toast.success("Follow-up completed");
      fetchPlan();
    } catch { toast.error("Failed to complete follow-up"); }
  }

  if (loading) return (
    <div className="space-y-4">
      {[1,2,3].map(i => <div key={i} className="h-32 skeleton-shimmer rounded-xl" />)}
    </div>
  );

  if (!plan) return <div className="text-center py-12 text-[#6B7280]">Failed to load planner</div>;

  const targetEntries = Object.entries(plan.targets || {});
  const actualEntries = plan.actuals || {};

  return (
    <div className="space-y-6" data-testid="daily-planner-page">
      {/* Greeting */}
      <div className="bg-gradient-to-r from-[#0F172A] to-[#1E293B] rounded-2xl p-6 md:p-8 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Sun size={28} className="text-[#C5A059]" />
          <h1 className="text-2xl md:text-3xl font-bold" data-testid="planner-greeting">{plan.greeting}</h1>
        </div>
        <p className="text-[#94A3B8] text-sm">
          {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          {[
            { label: "Follow-ups Due", value: plan.follow_ups_due?.length || 0, icon: Phone, color: plan.follow_ups_due?.length > 0 ? "#EF4444" : "#10B981" },
            { label: "Active Vacancies", value: plan.active_vacancies || 0, icon: Cube, color: "#C5A059" },
            { label: "In Pipeline", value: plan.pipeline_count || 0, icon: Kanban, color: "#8B5CF6" },
            { label: "Interviews This Week", value: plan.upcoming_interviews?.length || 0, icon: CalendarCheck, color: "#06B6D4" },
          ].map(stat => (
            <div key={stat.label} className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
              <stat.icon size={20} style={{ color: stat.color }} />
              <div className="text-2xl font-bold mt-1">{stat.value}</div>
              <div className="text-xs text-[#94A3B8]">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Follow-ups Due */}
        <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden" data-testid="followups-section">
          <div className="px-5 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
            <h2 className="font-bold text-[#0F172A] flex items-center gap-2">
              <Phone size={18} className="text-[#EF4444]" /> Follow-ups Due
              {plan.follow_ups_due?.length > 0 && (
                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold">{plan.follow_ups_due.length}</span>
              )}
            </h2>
          </div>
          <div className="divide-y divide-[#F1F5F9] max-h-80 overflow-y-auto">
            {(!plan.follow_ups_due || plan.follow_ups_due.length === 0) ? (
              <div className="p-6 text-center">
                <CheckCircle size={32} className="text-green-400 mx-auto mb-2" />
                <p className="text-sm text-[#6B7280]">No follow-ups due today</p>
              </div>
            ) : plan.follow_ups_due.map(fu => (
              <div key={fu.id} className="px-5 py-3 flex items-center justify-between hover:bg-[#F9FAFB]" data-testid={`followup-${fu.id}`}>
                <div>
                  <div className="text-sm font-medium text-[#0F172A]">{fu.contact_name || fu.phone_number || "Unknown"}</div>
                  <div className="text-xs text-[#6B7280]">
                    {fu.follow_up_date} {fu.follow_up_time && `at ${fu.follow_up_time}`}
                    {fu.notes && ` — ${fu.notes}`}
                  </div>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs gap-1 text-green-600 hover:text-green-700 hover:bg-green-50"
                    onClick={() => completeFollowUp(fu.id)}
                    data-testid={`complete-followup-${fu.id}`}
                  >
                    <Check size={12} /> Done
                  </Button>
                </div>
              </div>
            ))}
          </div>
          {plan.upcoming_followups?.length > 0 && (
            <>
              <div className="px-5 py-3 border-t border-[#E5E7EB] bg-[#F9FAFB]">
                <span className="text-xs font-semibold text-[#6B7280] uppercase">Upcoming This Week</span>
              </div>
              {plan.upcoming_followups.slice(0, 5).map(fu => (
                <div key={fu.id} className="px-5 py-2.5 flex items-center justify-between text-sm border-t border-[#F1F5F9]">
                  <div>
                    <span className="text-[#0F172A] font-medium">{fu.contact_name || fu.phone_number || "Unknown"}</span>
                    <span className="text-xs text-[#9CA3AF] ml-2">{fu.follow_up_date}</span>
                  </div>
                  <Clock size={14} className="text-[#D1D5DB]" />
                </div>
              ))}
            </>
          )}
        </div>

        {/* Daily Targets */}
        <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden" data-testid="targets-section">
          <div className="px-5 py-4 border-b border-[#E5E7EB]">
            <h2 className="font-bold text-[#0F172A] flex items-center gap-2">
              <Target size={18} className="text-[#C5A059]" /> Daily Targets
            </h2>
          </div>
          <div className="p-5 space-y-4">
            {targetEntries.length === 0 ? (
              <p className="text-sm text-[#6B7280] text-center py-4">No targets configured</p>
            ) : targetEntries.map(([key, target]) => {
              const actual = actualEntries[key] || 0;
              const pct = target > 0 ? Math.min(100, Math.round((actual / target) * 100)) : 0;
              const label = key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
              return (
                <div key={key} data-testid={`target-${key}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium text-[#374151]">{label}</span>
                    <span className="text-sm font-bold" style={{ color: pct >= 100 ? "#16A34A" : pct >= 50 ? "#C5A059" : "#6B7280" }}>
                      {actual}/{target}
                    </span>
                  </div>
                  <div className="w-full bg-[#F1F5F9] rounded-full h-2">
                    <div
                      className="rounded-full h-2 transition-all duration-500"
                      style={{
                        width: `${pct}%`,
                        backgroundColor: pct >= 100 ? "#16A34A" : pct >= 50 ? "#C5A059" : "#94A3B8",
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Upcoming Interviews */}
        <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden" data-testid="interviews-section">
          <div className="px-5 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
            <h2 className="font-bold text-[#0F172A] flex items-center gap-2">
              <CalendarCheck size={18} className="text-[#06B6D4]" /> Upcoming Interviews
            </h2>
            <Button variant="ghost" size="sm" className="text-xs gap-1" onClick={() => navigate("/hub/interviews")}>
              View All <ArrowRight size={12} />
            </Button>
          </div>
          <div className="divide-y divide-[#F1F5F9]">
            {(!plan.upcoming_interviews || plan.upcoming_interviews.length === 0) ? (
              <div className="p-6 text-center text-sm text-[#6B7280]">No interviews scheduled this week</div>
            ) : plan.upcoming_interviews.map(int => (
              <div key={int.id} className="px-5 py-3 hover:bg-[#F9FAFB]">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-[#0F172A]">{int.candidate_name}</span>
                    <span className="text-xs text-[#6B7280] ml-2">{int.company_name}</span>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded bg-[#F0F9FF] text-[#0369A1] font-medium">{int.interview_type}</span>
                </div>
                <div className="text-xs text-[#9CA3AF] mt-1">
                  {int.scheduled_at && new Date(int.scheduled_at).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                  {int.meeting_platform && ` via ${int.meeting_platform}`}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden" data-testid="quick-actions">
          <div className="px-5 py-4 border-b border-[#E5E7EB]">
            <h2 className="font-bold text-[#0F172A] flex items-center gap-2">
              <Lightning size={18} className="text-[#F59E0B]" /> Quick Actions
            </h2>
          </div>
          <div className="p-5 grid grid-cols-2 gap-3">
            {[
              { label: "Open Dialer", icon: Phone, nav: "/dialer", color: "#16A34A" },
              { label: "View Pipeline", icon: Kanban, nav: "/pipeline", color: "#8B5CF6" },
              { label: "Companies", icon: Buildings, nav: "/companies", color: "#1E3A8A" },
              { label: "Sector Hub", icon: Target, nav: "/sector-hub", color: "#C5A059" },
              { label: "Scripts", icon: Lightning, nav: "/scripts", color: "#F59E0B" },
              { label: "Academy", icon: Users, nav: "/academy", color: "#06B6D4" },
            ].map(action => (
              <button
                key={action.label}
                onClick={() => navigate(action.nav)}
                className="flex items-center gap-3 p-3 rounded-lg border border-[#E5E7EB] hover:border-[#C5A059] hover:bg-[#FFFBEB] transition-all text-left"
                data-testid={`quick-${action.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <action.icon size={20} style={{ color: action.color }} />
                <span className="text-sm font-medium text-[#374151]">{action.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      {plan.recent_activity?.length > 0 && (
        <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden" data-testid="recent-activity">
          <div className="px-5 py-4 border-b border-[#E5E7EB]">
            <h2 className="font-bold text-[#0F172A] flex items-center gap-2">
              <ArrowClockwise size={18} className="text-[#6B7280]" /> Recent Activity
            </h2>
          </div>
          <div className="divide-y divide-[#F1F5F9]">
            {plan.recent_activity.slice(0, 8).map((act, idx) => (
              <div key={idx} className="px-5 py-2.5 flex items-center justify-between text-sm">
                <div>
                  <span className="text-[#0F172A]">{String(act.action || "").replace(/_/g, " ")}</span>
                  {act.details && typeof act.details === "string" && <span className="text-[#9CA3AF] ml-2">{act.details}</span>}
                </div>
                <span className="text-xs text-[#9CA3AF] shrink-0">
                  {act.created_at && new Date(act.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
